/**
 * The font-pairing vocabulary, shared by every surface that has to name a face:
 * the My Agency picker, the client-v2 portal's web-font loader, and the PDF
 * template generator's react-pdf font bridge.
 *
 * `key` is the value persisted to `agency.fontPairing`. Each pairing names a
 * `heading` face (display — headings and money figures) and a `body` face
 * (labels and running text).
 */

/**
 * One family at the weights the font CDN serves as a latin-subset WOFF2.
 * `slug` is the CDN folder name and woff2 file-name stem, e.g. 'crimson-pro'.
 * @typedef {{ readonly family: string, readonly slug: string, readonly weights: readonly number[], readonly preload: number }} PairingFace
 */

/**
 * A pairing's two roles: display face for headings, body face for running text.
 * @typedef {{ readonly key: string, readonly heading: PairingFace, readonly body: PairingFace }} PairingEntry
 */

/** Host serving the shared font store, in every environment. */
export const FONT_CDN = 'https://cdn-static.itrvl.com';

/**
 * URL of a family's latin-subset WOFF2 at one weight, normal style. `cdn` names
 * another host serving the same store.
 * @param {{ slug: string, weight: number, cdn?: string }} face
 * @returns {string}
 */
export const fontFaceUrl = ({ slug, weight, cdn = FONT_CDN }) => `${cdn}/fonts/${slug}/${slug}-latin-${weight}-normal.woff2`;

/**
 * Pairings in picker order. `preload` is the face the portal emits as `<link rel="preload">`.
 * @type {readonly PairingEntry[]}
 */
export const FONT_PAIRINGS_FACES = [
  {
    key: '00',
    heading: { family: 'Outfit', slug: 'outfit', weights: [400, 500, 600, 700], preload: 600 },
    body: { family: 'Inter', slug: 'inter', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '01',
    // Athelas ships a single web-licensed weight (400); there is no upright bold,
    // so headings render at 400.
    heading: { family: 'Athelas', slug: 'athelas', weights: [400], preload: 400 },
    body: { family: 'Atyp Display', slug: 'atyp-display', weights: [300, 400, 500], preload: 400 },
  },
  {
    key: '02',
    heading: { family: 'Crimson Pro', slug: 'crimson-pro', weights: [400, 500, 600, 700], preload: 600 },
    body: { family: 'Inter', slug: 'inter', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '03',
    heading: { family: 'Cormorant Garamond', slug: 'cormorant-garamond', weights: [400, 500, 600, 700], preload: 600 },
    body: { family: 'Montserrat', slug: 'montserrat', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '04',
    // DM Serif Display ships a single weight (400); the display scale faux-bolds it.
    heading: { family: 'DM Serif Display', slug: 'dm-serif-display', weights: [400], preload: 400 },
    body: { family: 'Manrope', slug: 'manrope', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '05',
    heading: { family: 'Lora', slug: 'lora', weights: [400, 500, 600, 700], preload: 600 },
    body: { family: 'Work Sans', slug: 'work-sans', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '06',
    heading: { family: 'Spectral', slug: 'spectral', weights: [400, 500, 600, 700], preload: 600 },
    body: { family: 'Karla', slug: 'karla', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '07',
    // Rector Medium is weight 500 — the only Rector face the design uses, and the
    // weight the Yellow Zebra display scale pins.
    heading: { family: 'Rector', slug: 'rector', weights: [500], preload: 500 },
    body: { family: 'Figtree', slug: 'figtree', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '08',
    heading: { family: 'New Spirit', slug: 'new-spirit', weights: [300, 400], preload: 400 },
    body: { family: 'Circular Pro', slug: 'circular-pro', weights: [400, 700], preload: 400 },
  },
  {
    key: '09',
    heading: { family: 'Chivo', slug: 'chivo', weights: [400, 500, 600, 700], preload: 600 },
    body: { family: 'Public Sans', slug: 'public-sans', weights: [400, 500, 600, 700], preload: 400 },
  },
  {
    key: '10',
    heading: { family: 'Libre Baskerville', slug: 'libre-baskerville', weights: [400, 500, 600, 700], preload: 600 },
    body: { family: 'Libre Franklin', slug: 'libre-franklin', weights: [400, 500, 600, 700], preload: 400 },
  },
];

const FACES_BY_KEY = new Map(FONT_PAIRINGS_FACES.map(pairing => [pairing.key, pairing]));

/**
 * The pairing's two faces, or `undefined` for an absent or unknown key.
 * @param {string | null | undefined} key
 * @returns {PairingEntry | undefined}
 */
export const getPairingFaces = key => FACES_BY_KEY.get(key);

/**
 * Font-pairing options offered in the My Agency picker, one per pairing. `value`
 * is the key persisted to `agency.fontPairing`; client-v2 resolves it to the
 * heading + body families.
 */
export const FONT_PAIRING_OPTIONS = FONT_PAIRINGS_FACES.map(({ key, heading, body }) => ({
  value: key,
  label: `${heading.family} + ${body.family}`,
}));

export const FONT_PAIRING_KEYS = FONT_PAIRING_OPTIONS.map(option => option.value);
